/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';

import template from './creative-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {CreativeBulkActionService} from './creative-bulk-action.service';
import {CreativeAttachModalService} from './creative-attach-modal.service';
import {CreativeCopyInBulkModalService} from './creative-copy-in-bulk-modal.service';
import {asUrlGroupList, asUrlList} from './creative-url-utils';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-creative-list-page',
  template
})
export class CreativeListPageComponent extends AdminResourceListPageBase implements OnInit, OnChanges {
  @Input() readOnly: boolean;
  @Input() calledFrom: string;

  readonly itemStateName = 'app.adv.creatives.creative';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly lineItemItemStateName = 'app.adv.line-items.line-item';
  readonly creativeSetItemStateName = 'app.adv.creative-sets.creative-set';
  readonly externalDemandSourceItemStateName = 'app.adm.external-demand-sources.external-demand-source';

  hasCreativeSet = false;
  attachSuffix = 'Line Item';

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(CreativeResource) private readonly Creative: any,
    private readonly creativeBulkActionService: CreativeBulkActionService,
    private readonly creativeAttachModalService: CreativeAttachModalService,
    private readonly creativeCopyInBulkModalService: CreativeCopyInBulkModalService
  ) {
    super(LocalNetworkProfile);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.hasCreativeSet = LocalNetworkInfo.obtain().getInfo('creativeSetCount') > 0;
    this.attachSuffix = 'Line Item' + (this.hasCreativeSet ? ' or Creative Set' : '');
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.calledFrom) {
      this.afterListInit();
    }
  }

  get visibleColumns(): AdminListColumn[] {
    const fields = this.list?.sel?.outputFields?.length
      ? this.list.sel.outputFields
      : (this.list?.defaultFields || []);
    return this.normalizeColumns(fields);
  }

  isFieldVisible(param: string): boolean {
    return this.isListFieldVisible(param);
  }

  layoutLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_LAYOUTS !== true;
  }

  previewParams(creative: any): any {
    return {
      creativeId: creative.id,
      networkId: creative.network?.id,
      creativeWidth: creative.width,
      creativeHeight: creative.height,
      lineItemId: creative.lineItemId || creative.lineItem?.id,
      lineItemCode: creative.lineItemCode || creative.lineItem?.lineItemCode
    };
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }

  impressionTrackingUrlList(creative: any): string[] {
    return asUrlList(creative?.impressionTrackingUrls);
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.list.canExpand = false;

    if (!this.list.copyInBulk) {
      this.list.copyInBulk = () => {
        this.creativeCopyInBulkModalService.open(this.list, this.Creative, () => {
          this.list.makeQuery();
        });
      };
    }

    if (!this.list.attach) {
      this.list.attach = () => {
        this.creativeAttachModalService.open(this.list, this.Creative, () => {
          this.list.makeQuery();
        });
      };
    }

    if (!this.list.getChildren) {
      getListChildren(this.list, this.adnListHelper, OBJECT_TYPE.Creative, this.Creative);
    }

    if (!this.list.bulkAction) {
      this.list.bulkAction = this.creativeBulkActionService.createBulkAction(this.list);
    }
  }
}
